// Sharing a saved night: one link per night (/n/<token>), handed to the native share sheet, else copied.
import { APP_URL } from '../config'
import { shareNight } from './api'

const tokens = new Map<string, string>()

export const nightLink = (token: string) => `${APP_URL}/n/${token}`

/** The token is fetched once per night per visit, so a second tap can share without waiting (Safari's gesture rule). */
export async function nightShareUrl(nightId: string): Promise<string> {
  let tok = tokens.get(nightId)
  if (!tok) {
    tok = (await shareNight(nightId)).share_token
    if (!tok) throw new Error('no share token')
    tokens.set(nightId, tok)
  }
  return nightLink(tok)
}

export const cachedNightUrl = (nightId: string) => (tokens.has(nightId) ? nightLink(tokens.get(nightId)!) : null)

/** A hidden textarea and the old copy command: desktop browsers that refuse the async clipboard still allow this. */
function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.setAttribute('aria-hidden', 'true')
  ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;opacity:0;pointer-events:none'
  const before = document.activeElement as HTMLElement | null
  document.body.appendChild(ta)
  ta.focus({ preventScroll: true })
  ta.select()
  ta.setSelectionRange(0, text.length)
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  ta.remove()
  before?.focus?.({ preventScroll: true })
  return ok
}

/** Copy text: the async clipboard, else the hidden-textarea copy. false = neither worked (show it to copy by hand). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* blocked or not allowed here: the old way below */
  }
  return legacyCopy(text)
}

/**
 * A touch screen (press and hold to copy) rather than a keyboard and pointer (Ctrl+C / Cmd+C): a coarse pointer, or
 * a phone / tablet by its touch points (iPadOS reports a Mac, with touch). A touch laptop counts as a keyboard.
 */
export function touchScreen(): boolean {
  if (typeof window === 'undefined') return false
  const mq = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches
  if (mq('(pointer: coarse)')) return true
  const ua = navigator.userAgent ?? ''
  const touch = (navigator.maxTouchPoints ?? 0) > 0
  return touch && (/iPhone|iPad|iPod|Android|Mobile/.test(ua) || ((navigator.maxTouchPoints ?? 0) > 1 && /Macintosh/.test(ua)))
}

/**
 * 'shared': the share sheet took it. 'copied': on the clipboard. 'cancelled': they closed the sheet.
 * 'manual': neither worked (no gesture left, no clipboard): show the link so it can be copied by hand.
 */
export async function shareOrCopy(url: string, title: string): Promise<'shared' | 'copied' | 'cancelled' | 'manual'> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, url })
      return 'shared'
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') return 'cancelled'
      // NotAllowedError (the tap's moment passed while the link loaded): fall through to copying
    }
  }
  return (await copyText(url)) ? 'copied' : 'manual'
}
